import { createElement, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../util/testId';
import { TabBar, TabBarVariant, TabItem } from '../TabBar';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { BasicColorPicker, recentStandardColors, setRecentStandardColors } from './__internal__/';
import { useColorPickerService } from './ColorPickerProvider';
import { CustomColorPicker } from './__internal__/';
import { useConfig } from '../../hooks/useConfig';
import { DefaultColorPickerProps } from './types';
import { useLanguage } from '../../locale';
import { Flow } from '../Layouts/Flow';
import { ColorObject } from '.';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import { useMounted } from '../../hooks/useMounted';
import { createUseHtmlId } from '../../hooks/useHtmlId';

import cx from 'classnames';
import bundle from './NovaComponents-ColorPicker-gui-icu.properties';
import cn from './DefaultColorPicker.css.json';
import './DefaultColorPicker.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('DefaultColorPicker');
const logger = getLogger('DefaultColorPicker');

export const DefaultColorPicker = forwardRef<HTMLDivElement, DefaultColorPickerProps>(
  ({ hideCustomTabActionButtons = false, ...props }, ref) => {
    const localize = useTextLocalizer();

    const newSwatchColorRef = useRef<ColorObject>(props.newColor);

    const setNewSwatchColor = useCallback((newColor: ColorObject) => {
      newSwatchColorRef.current = newColor;
    }, []);

    const lastSelectedColor = props.lastSelectedColor ?? { color: '#000000', type: 'standard' };

    const currentColor = props.selectedColor;
    const newColor = hideCustomTabActionButtons ? props.newColor : newSwatchColorRef.current;

    const [basicTabPanelId, customTabPanelId] = useHtmlId(2);
    const tabItems: TabItem[] = [
      { label: localize('basicTab.label'), ariaControls: basicTabPanelId },
      { label: localize('customTab.label'), ariaControls: customTabPanelId },
    ];

    const dt = useDesignTokens();
    const lang = useLanguage();

    const locale = lang.toLowerCase();
    const getLanguageWidth = () => {
      switch (locale) {
        case 'de-de-pseudo':
        case 'it-it-pseudo':
          return dt.themeVariant === 'high-contrast' ? '25rem' : '22rem';
        case 'es-es-pseudo':
        case 'ru-ru-pseudo':
          return dt.themeVariant === 'high-contrast' ? '25.5rem' : '22rem';
        case 'fr-fr-pseudo':
          return dt.themeVariant === 'high-contrast' ? '24.5rem' : '21.5rem';
        case 'pl-pl-pseudo':
          return dt.themeVariant === 'high-contrast' ? '24.5rem' : '21rem';
        case 'lt-lt-pseudo':
          return dt.themeVariant === 'high-contrast' ? '24rem' : '20.5rem';
        case 'zh-cn-pseudo':
          return '20rem';
        case 'pl-xx':
          return dt.themeVariant === 'high-contrast' ? '21rem' : '17.5rem';
        default:
          return '12rem';
      }
    };

    const getLanguageHeight = () => {
      switch (locale) {
        case 'zh-cn-pseudo':
          if (props.numberOfRecentCustomRows && props.numberOfRecentCustomRows >= 2) {
            return hideCustomTabActionButtons ? '25rem' : '30rem';
          }
        default:
          return hideCustomTabActionButtons ? '25rem' : '28rem';
      }
    };
    const tokens = renderInlineCssVars(
      {
        'spacing-m': dt.spacing.core.m,
        height: getLanguageHeight(),
        width: getLanguageWidth(),
      },
      'color-picker'
    );

    const appId = useConfig()?.get('appId');
    const service = props.dataProvider ?? useColorPickerService();
    const storeRecentCustomColorsPerApplication =
      props.storeRecentCustomColorsPerApplication ?? true;
    const [recentCustomColors, setRecentCustomColors] = useState<string[]>([]);
    const mounted = useMounted();

    const onBasicTabColorSelect = (color: ColorObject) => {
      props.onColorSelect(color);

      if (!hideCustomTabActionButtons && color.type === 'standard' && color.color !== '#000000') {
        persistStandardColor(color);
      }
    };

    const persistStandardColor = (color: ColorObject) => {
      if (!recentStandardColors.includes(color.color)) {
        setRecentStandardColors([color.color, ...recentStandardColors].slice(0, 7));
      }
    };

    const persistCustomColor = async (color: ColorObject) => {
      const selectedColorIndex = recentCustomColors.indexOf(color.color);
      let recentColors = recentCustomColors.slice(0, 20);

      if (
        selectedColorIndex === -1 ||
        selectedColorIndex >= (props.numberOfRecentCustomRows ?? 1) * 7
      ) {
        if (selectedColorIndex >= (props.numberOfRecentCustomRows ?? 1) * 7) {
          recentColors.splice(selectedColorIndex, 1);
        }
        recentColors = [color.color, ...recentColors];
        try {
          await service?.setRecentCustomColors(
            recentColors,
            storeRecentCustomColorsPerApplication ? appId : null
          );
          // state update incase this component is used outside ColorPicker
          if (mounted.current) setRecentCustomColors(recentColors);
        } catch (error) {
          logger.error(
            `The call to set recent custom colors ${
              storeRecentCustomColorsPerApplication ? 'using '.concat(appId) : ''
            } has failed`
          );
        }
      }
    };

    const onCustomTabColorSelect = (color: ColorObject) => {
      if (!hideCustomTabActionButtons && color.color !== '#000000') {
        if (color.type === 'standard') {
          persistStandardColor(color);
        } else {
          persistCustomColor(color);
        }
      }
      props.onColorSelect(color);
    };

    useEffect(() => {
      let mounted = true;
      const fetchRecentCustomColors = async () => {
        if (!storeRecentCustomColorsPerApplication && service) {
          try {
            const storedCustomColors = await service.getRecentCustomColors();

            if (mounted && !isNullish(storedCustomColors)) {
              setRecentCustomColors(storedCustomColors);
            }
          } catch (error) {
            logger.error(
              `The call to get recent custom colors per user across applications has failed`
            );
          }
        }
      };
      fetchRecentCustomColors();
      return () => {
        mounted = false;
      };
    }, [service, storeRecentCustomColorsPerApplication]);

    useEffect(() => {
      let mounted = true;
      const fetchRecentCustomColors = async () => {
        if (service && storeRecentCustomColorsPerApplication && appId) {
          try {
            const storedCustomColors = await service.getRecentCustomColors(appId);

            if (mounted && !isNullish(storedCustomColors)) {
              setRecentCustomColors(storedCustomColors);
            }
          } catch (error) {
            logger.error(
              `The call to get recent custom colors for application ${appId} has failed`
            );
          }
        }
      };
      fetchRecentCustomColors();
      return () => {
        mounted = false;
      };
    }, [appId, service, storeRecentCustomColorsPerApplication]);

    return (
      <Flow
        ref={ref}
        style={{ ...tokens, ...props.style }}
        orientation="vertical"
        data-testid={props.testId}
        className={cx(cn.defaultColorPickerContainer, props.className)}
      >
        <Flow
          data-testid={postfixTestId(props.testId, 'tabbarContainer')}
          className={cn.tabbarContainer}
          spacing="s"
        >
          <TabBar
            testId={postfixTestId(props.testId, 'tabbar')}
            stretchToFit
            variant={TabBarVariant.Standard}
            visualStyle={'standard-small'}
            selectedIndex={props.selectedIndex}
            onItemSelected={({ index }) => {
              props.onTabItemSelect(index);
            }}
            items={tabItems}
          />
        </Flow>
        {props.selectedIndex === 0 ? (
          <BasicColorPicker
            id={basicTabPanelId}
            testId={postfixTestId(props.testId, 'basicColorPicker')}
            onColorSelect={onBasicTabColorSelect}
            renderAutomaticSwatch={props.renderAutomaticSwatch}
            selectedColor={currentColor}
            newColor={newColor}
            lastSelectedColor={lastSelectedColor}
            automaticColor={props.automaticColor}
            hideNoColor={props.hideNoColor}
            showColorIndicator={props.showColorIndicator ?? hideCustomTabActionButtons}
            recentCustomColors={recentCustomColors}
            numberOfRecentCustomRows={props.numberOfRecentCustomRows}
            hideAutomaticColor={props.hideAutomaticColor}
          />
        ) : (
          <CustomColorPicker
            id={customTabPanelId}
            testId={postfixTestId(props.testId, 'customColorPicker')}
            selectedColor={currentColor}
            renderAutomaticSwatch={props.renderAutomaticSwatch}
            newColor={newColor}
            lastSelectedColor={lastSelectedColor}
            onColorSelect={onCustomTabColorSelect}
            hideCustomTabActionButtons={hideCustomTabActionButtons}
            {...(!hideCustomTabActionButtons && {
              onCancelPress: props.onCancelPress,
              onNewColorChange: setNewSwatchColor,
            })}
          />
        )}
      </Flow>
    );
  }
);
